import { createSlice, createSelector } from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

export const getExerciseList = createAsyncThunk(
  "exercise/getExerciseList",
  async ({ token }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`/api/account/listExercises`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// ── Phase 9 (doc 29, P9.9) — prescription source + reasoning per movement ─────
//
// Reads the contract-v2 prefill endpoint (GET /api/exercise-prefill), the ONE
// web-reachable response that carries `source` (incl. 'progression') and the
// `reasoning` string the worker engine produced. This is the eval-surfacing
// input: it tells us, per movement, whether the next session was prescribed by
// the progression engine and why.
//
// NOTE on override-rate: the prefill endpoint returns only the prescribed
// `target_*` for the NEXT session — it does NOT return the per-set `actual_*`
// of confirmed sets, so the target-vs-actual override signal is NOT yet
// web-reachable from any existing endpoint (the worker's getMovementHistory
// has both target and actual but is unexposed). The override-rate selector
// below therefore computes from per-set {target_*, actual_*} IF a future
// endpoint provides them, and otherwise reports a `pending` status so the UI
// can show a clear data-pending state. See PR Follow-ups for the worker dep.
export const getExercisePrescription = createAsyncThunk(
  "exercise/getExercisePrescription",
  async ({ token, exercise_name }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/exercise-prefill`, {
        params: { exercise_name },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      return { exercise_name, prescription: response.data };
    } catch (error) {
      return rejectWithValue(error?.response?.data ?? { error: "request failed" });
    }
  }
);

// ── Phase 9 (doc 29, P9.9 — admin eval aggregate) ────────────────────────────
//
// The headline eval metric is the OVERRIDE RATE PER MOVEMENT across the cohort:
// the share of confirmed prescribed sets whose final actual_* deviated from the
// prescribed target_* (doc 29 §F). That is a server-side aggregate over many
// users' set logs — it must come from a worker ADMIN endpoint, not the
// per-user prefill above.
//
// LIVE WORKER ENDPOINT: `GET /api/admin/prescriptions` is shipped (worker PR
// #140-era lane, on main + deployed to prod) and returns this aggregate —
// verified in prod returning HTTP 200 { window_days, movements: [...] }. This
// thunk fetches it via the proxy (functions/api/admin/getPrescriptions). On
// success it fulfils with the payload; on failure it rejects with the error so
// the page can surface a real error (vs. a healthy-but-empty window).
//
// Expected response shape (what the worker should return):
//   {
//     window_days: 30,
//     movements: [
//       {
//         exercise_name: "Back Squat",
//         total_prescribed: 128,          // confirmed prescribed sets in window
//         sources: { progression: 80, set_logs: 30, scalar_history: 10, default: 8 },
//         override_rate: 0.21,            // OPTIONAL: server-computed share
//         overrides: 27,                  // OPTIONAL: # sets actual != target (±epsilon)
//         mean_abs_weight_delta: 4.2,     // OPTIONAL
//         // OR per-set rows so the client can compute via computeOverrideRate:
//         sets: [{ target_reps, target_weight, actual_reps, actual_weight }, ...]
//       },
//       ...
//     ]
//   }
export const getPrescriptionEval = createAsyncThunk(
  "exercise/getPrescriptionEval",
  // `envParams` is the admin-console env routing param ({} in prod — today's
  // exact behaviour — or { env: 'staging' } to route this admin fetch to the
  // staging worker). Additive + backward-compatible: prod callers omit it.
  async ({ token, days = 30, envParams = {} }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/admin/getPrescriptions`, {
        params: { days, ...envParams },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data ?? { error: "request failed" });
    }
  }
);

// Default deviation epsilon: a "small epsilon" beyond which a confirmed set's
// final actual_* counts as an override of the prescribed target_* (doc 29 §F).
export const OVERRIDE_EPSILON = { reps: 0, weight: 2.5 };

// Pure helper (exported for unit testing): given an array of per-set rows that
// each carry both a target and a final actual value, compute the override rate
// — the share of confirmed sets whose actual deviated from target beyond the
// epsilon. Returns { status: 'pending' } when the rows do not expose both
// target and actual for any set (the current real-world case), so callers can
// render a data-pending state instead of a misleading 0%.
export const computeOverrideRate = (sets, epsilon = OVERRIDE_EPSILON) => {
  if (!Array.isArray(sets) || sets.length === 0) {
    return { status: "pending", rate: null, overrides: 0, total: 0 };
  }
  const usable = sets.filter(
    (s) =>
      s &&
      (s.target_reps != null || s.target_weight != null) &&
      (s.actual_reps != null || s.actual_weight != null)
  );
  if (usable.length === 0) {
    return { status: "pending", rate: null, overrides: 0, total: 0 };
  }
  let overrides = 0;
  usable.forEach((s) => {
    const repDev =
      s.target_reps != null && s.actual_reps != null
        ? Math.abs(s.actual_reps - s.target_reps) > epsilon.reps
        : false;
    const weightDev =
      s.target_weight != null && s.actual_weight != null
        ? Math.abs(s.actual_weight - s.target_weight) > epsilon.weight
        : false;
    if (repDev || weightDev) overrides += 1;
  });
  return {
    status: "ready",
    rate: overrides / usable.length,
    overrides,
    total: usable.length,
  };
};

// Normalize one movement row from the admin aggregate into a uniform view
// model the table renders. Accepts either a server-computed override_rate OR
// raw per-set `sets` (computes via computeOverrideRate), and always derives a
// source breakdown with percentages. Resilient to partial payloads.
export const normalizeMovementEval = (m, epsilon = OVERRIDE_EPSILON) => {
  const sources = m?.sources && typeof m.sources === "object" ? m.sources : {};
  const sourceTotal = Object.values(sources).reduce(
    (sum, n) => sum + (Number(n) || 0),
    0
  );
  const sourceBreakdown = Object.entries(sources)
    .map(([source, count]) => ({
      source,
      count: Number(count) || 0,
      pct: sourceTotal > 0 ? (Number(count) || 0) / sourceTotal : 0,
    }))
    .sort((a, b) => b.count - a.count);

  // Prefer a server-computed rate; otherwise compute from per-set rows; else
  // fall back to a pending status so the UI never shows a misleading 0%.
  let override;
  if (typeof m?.override_rate === "number") {
    const total =
      m.total_prescribed ?? m.total ?? (Array.isArray(m.sets) ? m.sets.length : 0);
    const overrides =
      m.overrides ?? (total ? Math.round(m.override_rate * total) : 0);
    override = { status: "ready", rate: m.override_rate, overrides, total };
  } else {
    override = computeOverrideRate(m?.sets ?? [], epsilon);
  }

  return {
    exercise_name: m?.exercise_name ?? m?.name ?? "Unknown",
    total: m?.total_prescribed ?? m?.total ?? sourceTotal,
    sourceBreakdown,
    override,
    meanAbsWeightDelta:
      typeof m?.mean_abs_weight_delta === "number"
        ? m.mean_abs_weight_delta
        : null,
  };
};

const exerciseSlice = createSlice({
  name: "exercise",
  initialState: {
    exercises: null,
    exerciseObjects: null,
    loading: false,
    error: false,
    // Phase 9: keyed by exercise_name → contract-v2 prefill payload.
    prescriptions: {},
    prescriptionLoading: false,
    prescriptionError: false,
    // Phase 9 admin eval aggregate (P9.9): per-movement source breakdown +
    // override rate across the cohort. `prescriptionEvalError` carries a real
    // worker/proxy failure (rejected request) so the page can distinguish an
    // error from a healthy-but-empty window (fulfilled with 0 movements).
    prescriptionEval: null,
    prescriptionEvalLoading: false,
    prescriptionEvalError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getExerciseList.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getExerciseList.fulfilled, (state, action) => {
        const { exercises, exerciseObjects } = action.payload;
        state.exercises = exercises;
        state.exerciseObjects = exerciseObjects;
        state.loading = false;
        state.error = false;
      })
      .addCase(getExerciseList.rejected, (state, action) => {
        state.error = true;
        state.loading = false;
      })
      .addCase(getExercisePrescription.pending, (state) => {
        state.prescriptionLoading = true;
        state.prescriptionError = false;
      })
      .addCase(getExercisePrescription.fulfilled, (state, action) => {
        const { exercise_name, prescription } = action.payload;
        state.prescriptions[exercise_name] = prescription;
        state.prescriptionLoading = false;
        state.prescriptionError = false;
      })
      .addCase(getExercisePrescription.rejected, (state) => {
        state.prescriptionError = true;
        state.prescriptionLoading = false;
      })
      .addCase(getPrescriptionEval.pending, (state) => {
        state.prescriptionEvalLoading = true;
        state.prescriptionEvalError = null;
      })
      .addCase(getPrescriptionEval.fulfilled, (state, action) => {
        state.prescriptionEval = action.payload;
        state.prescriptionEvalLoading = false;
        state.prescriptionEvalError = null;
      })
      .addCase(getPrescriptionEval.rejected, (state, action) => {
        state.prescriptionEvalError =
          action.payload ?? { error: "request failed" };
        state.prescriptionEvalLoading = false;
      });
  },
});

// ── Selectors (additive) ──────────────────────────────────────────────────────
export const selectPrescription = (exercise_name) => (state) =>
  state.exerciseSlice?.prescriptions?.[exercise_name] ?? null;

// Override-rate per movement, computed client-side from the prescription's
// per-set rows. Today the prefill rows carry only `target_*` (no `actual_*`),
// so this resolves to { status: 'pending' } until a worker endpoint exposes the
// confirmed-set actuals (see PR Follow-ups). The compute is real and live the
// moment those fields arrive — no UI rework needed.
export const selectOverrideRate = (exercise_name) => (state) => {
  const p = state.exerciseSlice?.prescriptions?.[exercise_name];
  return computeOverrideRate(p?.sets ?? []);
};

// ── Admin eval aggregate selectors (P9.9) ────────────────────────────────────
export const selectPrescriptionEval = (state) =>
  state.exerciseSlice?.prescriptionEval ?? null;

export const selectPrescriptionEvalLoading = (state) =>
  !!state.exerciseSlice?.prescriptionEvalLoading;

export const selectPrescriptionEvalError = (state) =>
  state.exerciseSlice?.prescriptionEvalError ?? null;

// Normalized, render-ready rows for the admin table. Empty array when the
// window has no prescribed-set events yet (the page shows a neutral empty
// state) or before the first fetch. Memoized so the connected dashboard doesn't
// re-render on every dispatch (the map() would otherwise return a fresh array
// reference each call).
export const selectMovementEvals = createSelector(
  [selectPrescriptionEval],
  (data) => {
    const movements = Array.isArray(data?.movements) ? data.movements : [];
    return movements.map((m) => normalizeMovementEval(m));
  }
);

export default exerciseSlice;
